import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import { DatumId, PieTooltipProps, ResponsivePie } from "@nivo/pie";
import { Fragment, useMemo } from "react";

type TooltipPosition = "left" | "right";
type TooltipTheme = "light" | "dark";

export function PieChart(props: {
  dataset: { id: string | null; value: number | boolean }[];
  colors?: string[];
  loadingColor: Palette[];
  disabled?: boolean;
  tooltipPosition?: TooltipPosition;
  tooltipTheme?: TooltipTheme;
  tooltipValueFormat?: (value: number, id?: DatumId) => string;
  showEmptyStateTooltip?: boolean;
  emptyStateTooltipText?: string;
}) {
  const tid = useTestIds({});
  const {
    dataset,
    colors,
    loadingColor,
    disabled,
    tooltipTheme,
    tooltipPosition,
    tooltipValueFormat,
    emptyStateTooltipText,
  } = props;

  const Chart = useMemo(
    () => (
      <ResponsivePie
        data={dataset}
        innerRadius={0.5}
        colors={disabled ? loadingColor : colors}
        enableArcLabels={false}
        enableArcLinkLabels={false}
        isInteractive={!disabled}
        tooltip={customTooltip(tooltipTheme, tooltipPosition, tooltipValueFormat)}
      />
    ),
    [dataset, colors, loadingColor, disabled, tooltipTheme, tooltipPosition, tooltipValueFormat],
  );

  return emptyStateTooltipText ? (
    <Tooltip {...tid.dataTooltip} title={emptyStateTooltipText} placement="top" children={Chart} />
  ) : (
    <Fragment children={Chart} />
  );
}

function customTooltip(
  tooltipTheme: TooltipTheme = "light",
  tooltipPosition: TooltipPosition = "left",
  tooltipValueFormat: (value: number, id?: DatumId) => string = (value: number) => value.toString(),
) {
  return ({ datum }: PieTooltipProps<{ id: string | null; value: number | boolean }>) => {
    const { color, label, value, id } = datum;

    return tooltipTheme === "light" ? (
      <div
        css={
          Css.bgWhite.df.gap1.absolute.fwnw
            .topPx(-5)
            .px1.pyPx(0.5)
            .jcc.aic.borderRadius("2px")
            .boxShadow("0 0 1px 0 rgba(0, 0, 0, 0.5)")
            .if(tooltipPosition === "left").right2.else.left2.$
        }
      >
        <div css={Css.h1.w1.br100.bgColor(color).$} />
        <span css={Css.fg1.$}>
          {label}: <b>{tooltipValueFormat(value)}</b>
        </span>
      </div>
    ) : (
      <div css={Css.bgGray900.white.xs.df.mt1.p1.br4.bshBasic.maxwPx(300).tal.$}>{tooltipValueFormat(value, id)}</div>
    );
  };
}
